import React, { useState, useEffect } from "react";
import "./TutorCreationModal.css";

const TutorCreationModal = ({ onClose, onSave, tutor, isEditing }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    systemMessage: "", // Keep the internal field name for API compatibility
    temperature: 0.7,
    maxResponseWords: 1000, // Standard length (300-400 words)
    responseTone: "friendly",
    complexityLevel: "middle-school",
    includeExamples: true,
    allowFollowUpQuestions: true,
    includeReferences: false,
    isActive: true,
  });

  useEffect(() => {
    if (isEditing && tutor) {
      setFormData({
        name: tutor.name || "",
        description: tutor.description || "",
        systemMessage: tutor.systemMessage || "",
        temperature: tutor.temperature || 0.7,
        maxResponseWords: tutor.maxResponseWords || 1000,
        responseTone: tutor.responseTone || "friendly",
        complexityLevel: tutor.complexityLevel || "middle-school",
        includeExamples: tutor.includeExamples !== false,
        allowFollowUpQuestions: tutor.allowFollowUpQuestions !== false,
        includeReferences: tutor.includeReferences || false,
        isActive: tutor.isActive !== false,
      });
    }
  }, [isEditing, tutor]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const tutorData = {
        ...formData,
        temperature: parseFloat(formData.temperature),
        maxResponseWords: parseInt(formData.maxResponseWords, 10),
        maxTokens: Math.ceil(parseInt(formData.maxResponseWords, 10) * 1.33),
        model: "gpt-4o-mini",
      };
      await onSave(tutorData, isEditing ? tutor.id : null);
    } catch (error) {
      console.error("Error saving tutor:", error);
      alert("Failed to save tutor. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="tutor-creation-modal-backdrop">
      <div className="tutor-creation-modal">
        <div className="tutor-creation-modal-header">
          <h2>{isEditing ? "Edit Tutor" : "Create New Tutor"}</h2>
          <span
            className="tutor-creation-modal-close-button"
            onClick={isSubmitting ? undefined : onClose}
            style={{ cursor: isSubmitting ? "not-allowed" : "pointer" }}
          >
            &times;
          </span>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Tutor Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="E.g., Math Helper, Writing Assistant"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">
              Description (Visible to Students)
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              placeholder="A brief, clear explanation of what this tutor does. Example: 'A friendly math tutor specializing in algebra and calculus, perfect for high school students.'"
              className="form-control"
              disabled={isSubmitting}
            />
            <small>
              This description appears in the tutor list and helps students
              choose the right tutor for their needs.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="systemMessage">Teaching Instructions</label>
            <textarea
              id="systemMessage"
              name="systemMessage"
              value={formData.systemMessage}
              onChange={handleChange}
              required
              placeholder="Detailed instructions for how the tutor should teach. Example: 'You are a math tutor specializing in algebra. Always break down problems step by step. Verify student understanding before proceeding to new concepts.'"
              rows={5}
              className="form-control"
              disabled={isSubmitting}
            />
            <small>
              Be specific about the teaching style, knowledge domain, and
              approach to explaining concepts. While students don't see these
              instructions, they shape how the tutor understands and responds to
              questions.
            </small>
          </div>

          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="responseTone">Response Tone</label>
              <select
                id="responseTone"
                name="responseTone"
                value={formData.responseTone}
                onChange={handleChange}
                className="form-control"
                disabled={isSubmitting}
              >
                <option value="friendly">Friendly and Encouraging</option>
                <option value="formal">Formal and Professional</option>
                <option value="socratic">Socratic (Question-Based)</option>
                <option value="simple">Simple and Direct</option>
              </select>
              <small>How the AI should communicate with students</small>
            </div>

            <div className="form-group half-width">
              <label htmlFor="complexityLevel">Complexity Level</label>
              <select
                id="complexityLevel"
                name="complexityLevel"
                value={formData.complexityLevel}
                onChange={handleChange}
                className="form-control"
                disabled={isSubmitting}
              >
                <option value="elementary">Elementary School</option>
                <option value="middle-school">Middle School</option>
                <option value="high-school">High School</option>
                <option value="undergraduate">Undergraduate</option>
                <option value="graduate">Graduate Level</option>
              </select>
              <small>Target academic level for responses</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="temperature">
                Creativity Level: {formData.temperature}
              </label>
              <input
                type="range"
                id="temperature"
                name="temperature"
                min="0"
                max="1"
                step="0.1"
                value={formData.temperature}
                onChange={handleChange}
                className="form-control"
                disabled={isSubmitting}
              />
              <small>
                Lower for factual responses, higher for creative thinking
              </small>
            </div>

            <div className="form-group half-width">
              <label htmlFor="maxResponseWords">Response Length</label>
              <select
                id="maxResponseWords"
                name="maxResponseWords"
                value={formData.maxResponseWords}
                onChange={handleChange}
                className="form-control"
                disabled={isSubmitting}
              >
                <option value="500">Brief (~150-200 words)</option>
                <option value="1000">Standard (~300-400 words)</option>
                <option value="2000">Detailed (~600-800 words)</option>
                <option value="3000">Comprehensive (~1000-1200 words)</option>
              </select>
              <small>
                Choose how detailed you want the tutor's responses to be. The
                tutor will aim to complete its thoughts within the selected
                length. Actual response length may vary slightly to ensure
                complete and coherent answers.
              </small>
            </div>
          </div>

          <div className="form-row checkbox-container">
            <div className="form-group half-width">
              <div className="option-container">
                <input
                  type="checkbox"
                  id="includeExamples"
                  name="includeExamples"
                  checked={formData.includeExamples}
                  onChange={handleChange}
                />
                <label htmlFor="includeExamples">Include Examples</label>
              </div>
              <small>
                Include relevant examples in responses to aid understanding
              </small>
            </div>

            <div className="form-group half-width">
              <div className="option-container">
                <input
                  type="checkbox"
                  id="allowFollowUpQuestions"
                  name="allowFollowUpQuestions"
                  checked={formData.allowFollowUpQuestions}
                  onChange={handleChange}
                />
                <label htmlFor="allowFollowUpQuestions">
                  Suggest Follow-up Questions
                </label>
              </div>
              <small>
                AI will suggest follow-up questions to encourage deeper learning
              </small>
            </div>
          </div>

          <div className="form-row checkbox-container">
            <div className="form-group half-width">
              <div className="option-container">
                <input
                  type="checkbox"
                  id="includeReferences"
                  name="includeReferences"
                  checked={formData.includeReferences}
                  onChange={handleChange}
                />
                <label htmlFor="includeReferences">Include References</label>
              </div>
              <small>
                Add academic references/sources at the end of responses
              </small>
            </div>

            <div className="form-group half-width">
              <div className="option-container">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <label htmlFor="isActive">Active (Available to Students)</label>
              </div>
              <small>
                When active, students can use this tutor. Deactivate to hide it
                from students.
              </small>
            </div>
          </div>

          <div className="form-buttons">
            <button
              type="button"
              className="tutor-creation-modal-cancel-button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="tutor-creation-modal-save-button"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isEditing
                  ? "Updating..."
                  : "Creating..."
                : isEditing
                ? "Update Tutor"
                : "Create Tutor"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TutorCreationModal;
